<template>
  <q-card-section>
    <div id="intercom-occupancy-forecast" style="overflow-x: auto">
      <div style="width: 100%; height: 300px">
        <loading-overlay v-if="isLoading" />
        <line-chart v-else-if="hasChartData" :data="chartData" :options="chartOptions" />
        <slot v-else name="empty-state">
          <div
            class="full-width full-height flex flex-center text-center text-primary"
            style="font-size: 12px"
          >
            Not enough data to calculate
          </div>
        </slot>
      </div>
      <q-markup-table
        v-if="hasChartData"
        dense
        bordered
        flat
        class="q-mt-md"
        separator="horizontal"
      >
        <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
          <tr>
            <th align="left">Forecast</th>
            <th v-for="label in chartData.labels" :key="label as string">
              {{ label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(dataset, idx) in chartData.datasets"
            :key="idx"
            align="left"
            :class="{ 'text-bold': dataset.label === 'Mid' || dataset.label === 'Occupancy' }"
          >
            <td>{{ dataset.label }}</td>
            <td v-for="(value, subIdx) in dataset.data" :key="subIdx" align="center">
              {{ formatValue(value as number) }}
            </td>
          </tr>
        </tbody>
      </q-markup-table>
    </div>
  </q-card-section>
</template>

<script lang="ts">
import { type PropType, computed } from 'vue'
import type { PropertyDetailsResponse, OccupancyProjections } from 'common'
import { useRentProjections, useSinglePropertyRentProjection } from '@/queries/rentProjections.ts'
import LoadingOverlay from '@/components/MarketAnalytics/LoadingOverlay.vue'
import { Line as LineChart } from 'vue-chartjs'
import type { ChartData, ChartDataset, ChartOptions } from 'chart.js'
import { formatPercent, unitTypeColor } from '@/utils'
import { format, addMonths } from 'date-fns'

export default {
  name: 'OccupancyProjectionsChart',
  props: {
    subjectProperty: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    isSingleProperty: {
      type: Boolean,
      default: false
    },
    frequency: {
      type: String as PropType<'yearly' | 'monthly'>,
      default: 'yearly'
    }
  },
  components: {
    LoadingOverlay,
    LineChart
  },
  computed: {
    chartData(): ChartData<'line'> {
      if (!this.occupancyProjections) {
        return { labels: [], datasets: [] }
      }

      if (this.frequency === 'yearly') {
        return this.getYearlyChartData()
      } else {
        return this.getMonthlyChartData()
      }
    },
    hasChartData() {
      return this.chartData.datasets.length > 0
    },
    yAxisBounds() {
      if (!this.occupancyProjections) {
        return { min: 0, max: 100 }
      }

      const { forecast_occupancy, ci_lower, ci_upper, current_occupancy, seasonal_occupancy } =
        this.occupancyProjections

      const allValues = [(current_occupancy ?? 0) * 100]

      if (this.frequency === 'yearly') {
        const historicalValues = Object.values(this.lastFiveYearsAvgHistoricalOccupancy).map(
          (v) => v * 100
        )
        allValues.push(...historicalValues)

        allValues.push(
          ...Object.values(forecast_occupancy).map((v) => v * 100),
          ...Object.values(ci_lower).map((v) => v * 100),
          ...Object.values(ci_upper).map((v) => v * 100)
        )
      } else {
        // For monthly, get all seasonal values
        Object.values(seasonal_occupancy).forEach((yearlyData) => {
          allValues.push(...yearlyData.map((v) => v * 100))
        })
      }

      const minValue = Math.min(...allValues)
      const maxValue = Math.max(...allValues)

      // Add padding (5% of the range, minimum 2 percentage points)
      const range = maxValue - minValue
      const padding = Math.max(range * 0.05, 2)

      return {
        min: Math.max(0, minValue - padding),
        max: Math.min(100, maxValue + padding)
      }
    },
    chartOptions(): ChartOptions<'line'> {
      return {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          tooltip: {
            itemSort: (a, b) => (b.raw as number) - (a.raw as number),
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (context) => {
                const rawValue = context.parsed.y as number
                const val = this.formatValue(rawValue)
                return `${context.dataset.label}: ${val}`
              }
            },
            filter: (tooltipItem) => {
              if (this.frequency === 'yearly' && this.chartData.datasets.length === 3) {
                if (this.chartData.labels) {
                  const pointLabel = this.chartData.labels[tooltipItem.dataIndex]
                  const currentYear = new Date().getFullYear()
                  const pointYear = parseInt(pointLabel as string)
                  const isHistoricalPoint = pointYear <= currentYear
                  const primaryDatasetIndex = this.chartData.datasets.findIndex(
                    (dataset) => dataset.label === 'Mid'
                  )

                  if (isHistoricalPoint && tooltipItem.datasetIndex !== primaryDatasetIndex) {
                    return false
                  }
                } else {
                  return false
                }
              }
              return true
            }
          },
          legend: {
            display: false
          }
        },
        interaction: {
          mode: 'index',
          intersect: false
        },
        scales: {
          x: {
            grid: {
              drawTicks: false,
              drawOnChartArea: false
            },
            ticks: {
              autoSkip: true,
              maxRotation: 0,
              minRotation: 0
            }
          },
          y: {
            min: this.yAxisBounds.min,
            max: this.yAxisBounds.max,
            ticks: {
              callback: (value) => this.formatValue(value as number)
            }
          }
        }
      }
    },
    lastFiveYearsAvgHistoricalOccupancy(): { [year: string]: number } {
      if (!this.historicalOccupancy) {
        return {}
      }

      const currentYear = new Date().getFullYear()

      // Group by year and calculate averages for the last 5 years
      const lastFiveYears = this.historicalOccupancy.reduce(
        (acc, group) => {
          const groupYear = parseInt(group.date.slice(0, 4))

          // Only include data from the last 5 years
          if (groupYear >= currentYear - 5 && groupYear < currentYear) {
            if (!acc[groupYear]) {
              acc[groupYear] = {
                occupancy: 0,
                count: 0
              }
            }
            acc[groupYear].occupancy += group.occupancy
            acc[groupYear].count += 1
          }

          return acc
        },
        {} as { [year: number]: { occupancy: number; count: number } }
      )

      // Convert to averages with string keys (matching chart format)
      const result: { [year: string]: number } = {}
      Object.entries(lastFiveYears).forEach(([year, data]) => {
        result[year] = data.occupancy / data.count
      })

      return result
    }
  },
  setup(props) {
    if (props.isSingleProperty) {
      const lat = computed(() => props.subjectProperty?.lat ?? null)
      const lng = computed(() => props.subjectProperty?.lon ?? null)
      const isLeaseUp = computed(() => props.subjectProperty?.is_lease_up ?? false)
      const { occupancyProjections, historicalOccupancy, isLoading } =
        useSinglePropertyRentProjection(lat, lng, isLeaseUp)
      return { occupancyProjections, historicalOccupancy, isLoading }
    } else {
      const { occupancyProjections, historicalOccupancy, isLoading } = useRentProjections()
      return { occupancyProjections, historicalOccupancy, isLoading }
    }
  },
  methods: {
    formatValue(val: number | null): string {
      return val !== null ? formatPercent(val / 100) : ''
    },
    getYearlyChartData(): ChartData<'line'> {
      const { forecast_occupancy, ci_lower, ci_upper, current_occupancy } = this
        .occupancyProjections as OccupancyProjections
      const timeKeys = Object.keys(forecast_occupancy)

      if (timeKeys.length === 0) {
        return { labels: [], datasets: [] }
      }

      const currentDate = new Date()

      const historicalYears = Object.keys(this.lastFiveYearsAvgHistoricalOccupancy)
        .map((year) => parseInt(year))
        .sort((a, b) => a - b)

      const historicalDates = historicalYears.map((year) => new Date(year, 0, 1))
      const historicalValues = historicalYears.map(
        (year) => (this.lastFiveYearsAvgHistoricalOccupancy[year.toString()] || 0) * 100
      )

      // Get forecast data
      const dates = timeKeys.map((key) => {
        const years = parseInt(key.replace('y', ''))
        return addMonths(currentDate, years * 12)
      })

      const values = timeKeys.map((key) => (forecast_occupancy[key] || 0) * 100) // Convert to percentage
      const lower = timeKeys.map((key) => (ci_lower[key] || 0) * 100)
      const upper = timeKeys.map((key) => (ci_upper[key] || 0) * 100)

      // Combine historical and forecast data
      const allDates = [...historicalDates, currentDate, ...dates]
      const allValues = [...historicalValues, (current_occupancy || 0) * 100, ...values]
      const allLower = [...historicalValues, (current_occupancy || 0) * 100, ...lower]
      const allUpper = [...historicalValues, (current_occupancy || 0) * 100, ...upper]

      const labels = allDates.map((date) => format(date, 'yyyy'))
      const dateStrings = allDates.map((date) => format(date, 'yyyy-MM-dd'))

      return {
        labels,
        datasets: this.computeDataset(dateStrings, allLower, allValues, allUpper)
      }
    },
    getMonthlyChartData(): ChartData<'line'> {
      const { seasonal_occupancy, current_occupancy } = this
        .occupancyProjections as OccupancyProjections

      const yearKeys = Object.keys(seasonal_occupancy)

      if (yearKeys.length === 0) {
        return { labels: [], datasets: [] }
      }

      const currentDate = new Date()
      const allDates = [currentDate]
      const allValues = [(current_occupancy || 0) * 100]

      yearKeys.forEach((yearKey) => {
        const yearNum = parseInt(yearKey.replace('y', ''))
        const monthlyData = seasonal_occupancy[yearKey]

        monthlyData?.forEach((value, monthIndex) => {
          const monthDate = addMonths(currentDate, (yearNum - 1) * 12 + monthIndex + 1)
          allDates.push(monthDate)
          allValues.push(value * 100)
        })
      })

      const labels = allDates.map((date) => format(date, 'MMM yyyy'))
      const dateStrings = allDates.map((date) => format(date, 'yyyy-MM-dd'))

      // For monthly view, we don't have confidence intervals, so use the same values
      return {
        labels,
        datasets: this.computeDataset(dateStrings, allValues, allValues, allValues)
      }
    },
    computeDataset(
      dates: string[],
      lower: number[],
      values: number[],
      upper: number[]
    ): ChartDataset<'line'>[] {
      const bed = 4 // used for the color purple

      const today = format(new Date(), 'yyyy-MM-dd')
      const pastColor = this.$q.dark.isActive ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'
      const valueColors = dates.map((date) => (date <= today ? pastColor : unitTypeColor(bed, 0.7)))

      // For monthly view with no confidence intervals, only show the main line
      if (this.frequency === 'monthly') {
        return [
          {
            label: 'Occupancy',
            data: values,
            borderWidth: 3,
            pointRadius: 2,
            pointHoverRadius: 4,
            pointBackgroundColor: valueColors,
            pointBorderColor: valueColors,
            borderColor: unitTypeColor(bed, 0.7),
            backgroundColor: unitTypeColor(bed, 0.7),
            tension: 0.1,
            fill: false,
            segment: {
              borderColor: (ctx) => valueColors[ctx.p0DataIndex + 1] || unitTypeColor(bed, 0.7),
              backgroundColor: (ctx) => valueColors[ctx.p0DataIndex + 1] || unitTypeColor(bed, 0.7)
            }
          }
        ]
      }

      // For yearly view with confidence intervals
      return [
        {
          label: 'High',
          data: upper,
          borderColor: 'rgba(0, 0, 0, 0)',
          pointRadius: 0,
          fill: '+1',
          backgroundColor: unitTypeColor(bed, 0.3),
          pointHoverRadius: 3,
          tension: 0.1
        },
        {
          label: 'Mid',
          data: values,
          borderWidth: 3,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: valueColors,
          pointBorderColor: valueColors,
          borderColor: unitTypeColor(bed, 0.7),
          backgroundColor: unitTypeColor(bed, 0.7),
          tension: 0.1,
          segment: {
            borderColor: (ctx) => valueColors[ctx.p0DataIndex + 1] || unitTypeColor(bed, 0.7),
            backgroundColor: (ctx) => valueColors[ctx.p0DataIndex + 1] || unitTypeColor(bed, 0.7)
          }
        },
        {
          label: 'Low',
          data: lower,
          borderColor: 'rgba(0, 0, 0, 0)',
          pointRadius: 0,
          fill: '-1',
          backgroundColor: unitTypeColor(bed, 0.3),
          pointHoverRadius: 3,
          tension: 0.1
        }
      ]
    }
  }
}
</script>
